import moment from 'moment';
import { DATE_FORMAT } from 'framework/utils';
import { CreateBilledItem, ScheduleItem } from './billingTypes';
import { BillingType } from 'domain/shared/newProject';

export const isMigrateEnabled = false;

type NumericValue = number | string | null | undefined;

type BilledItemSource = {
  id?: string | null;
  type?: string;
  milestoneName?: string | null;
  changeRequestAlias?: string | null;
  position?: string | number;
  periodMonth?: string;
  startDate?: string;
  endDate?: string;
  amount?: NumericValue;
  currency?: string | null;
  hours?: NumericValue;
  plannedRegularHours?: NumericValue;
  plannedOvertimeHours?: NumericValue;
  previousPeriodAdjustmentHours?: NumericValue;
};

export const formatPeriod = (position: string | number | undefined, date: string | undefined): string => {
  if (!date) {
    return '';
  }
  const parsed = moment(date, DATE_FORMAT.API_ONLY_MONTH);
  if (!parsed.isValid()) {
    return '';
  }
  return `Period ${position} - ${parsed.format(DATE_FORMAT.MONTH)}`;
};

export const formatCustomPeriod = (startDate: string | undefined, endDate: string | undefined): string => {
  if (!startDate || !endDate) {
    return '';
  }
  const start = moment(startDate, DATE_FORMAT.API_ONLY_DATE);
  const end = moment(endDate, DATE_FORMAT.API_ONLY_DATE);

  if (!start.isValid() || !end.isValid()) {
    return '';
  }

  if (!start.isSame(end, 'year')) {
    return `${start.format(DATE_FORMAT.NATURAL_SHORT)} - ${end.format(DATE_FORMAT.NATURAL_SHORT)}`;
  }

  return `${start.format(DATE_FORMAT.DAY)} - ${end.format(DATE_FORMAT.NATURAL_SHORT)}`;
};

export const formatChangeRequestAlias = (changeRequestAlias: string | undefined | null): string => {
  if (!changeRequestAlias) {
    return '';
  }

  return `Change Request ${changeRequestAlias}`;
};

export const parseAmount = (amount: NumericValue): number => {
  if (typeof amount === 'string') {
    const parsed = parseFloat(amount);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return typeof amount === 'number' ? amount : 0;
};

const mapMilestoneToBilledItem = (item: BilledItemSource): CreateBilledItem => ({
  scheduleItemId: item.id || '',
  name: item.milestoneName || item.changeRequestAlias || '',
  amount: parseAmount(item.amount),
  currency: item.currency || '',
});

const getPeriodicName = (item: BilledItemSource): string => {
  if (item.changeRequestAlias) {
    return formatChangeRequestAlias(item.changeRequestAlias);
  }

  if (item.type === ScheduleItem.CUSTOM_PERIOD) {
    return formatCustomPeriod(item.startDate, item.endDate);
  }
  return formatPeriod(item.position, item.periodMonth);
};

const mapPeriodicToBilledItem = (item: BilledItemSource): CreateBilledItem => ({
  scheduleItemId: item.id || '',
  name: getPeriodicName(item),
  amount: parseAmount(item.amount),
  currency: item.currency || '',
  hours: typeof item.hours === 'number' ? item.hours : parseAmount(item.hours || 0),
});

const normalizeNumber = (value: NumericValue): number => {
  if (!value) {
    return 0;
  }
  const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const mapPrepaidPeriodicToBilledItem = (item: BilledItemSource): CreateBilledItem => {
  const plannedRegularHours = normalizeNumber(item.plannedRegularHours || 0);
  const plannedOvertimeHours = normalizeNumber(item.plannedOvertimeHours || 0);
  const previousPeriodAdjustmentHours = normalizeNumber(item.previousPeriodAdjustmentHours || 0);

  return {
    ...mapPeriodicToBilledItem(item),
    plannedRegularHours,
    plannedOvertimeHours,
    previousPeriodAdjustmentHours,
    hours: plannedRegularHours + plannedOvertimeHours + previousPeriodAdjustmentHours,
  };
};

export const mapToBilledItems = (data: BilledItemSource[] = [], billingType: BillingType): CreateBilledItem[] => {
  const mapper = {
    [BillingType.PERIODIC_PREPAID]: mapPrepaidPeriodicToBilledItem,
    [BillingType.PERIODIC_POSTPAID]: mapPeriodicToBilledItem,
    [BillingType.MILESTONE]: mapMilestoneToBilledItem,
  };

  return data.map(mapper[billingType || BillingType.MILESTONE]);
};
